import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { trackAnalyticsEvent } from '../lib/analytics';
import './SearchBar.css';
import CheckoutModal from './CheckoutModal';
import ReturningUserModal from './ReturningUserModal';

function normalizeRef(input) {
  return (input || '').replace(/[^A-Za-z0-9-]/g, '').toUpperCase();
}

const getAnonFlag = () => {
  try {
    if (typeof window !== 'undefined') {
      return window.sessionStorage.getItem('anonSearchUsed') === 'true';
    }
  } catch (err) {
    // ignore storage failures
  }
  return false;
};

const SearchBar = () => {
  const [refNumber, setRefNumber] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [subscriptionEmail, setSubscriptionEmail] = useState(() => localStorage.getItem('userEmail') || '');
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [returningModalOpen, setReturningModalOpen] = useState(false);
  const [anonSearchUsed, setAnonSearchUsed] = useState(getAnonFlag);

  const normalizedRef = useMemo(() => normalizeRef(refNumber), [refNumber]);
  const searchDisabled = loading || statusLoading;
  const searchButtonLabel = loading ? '🔄 Searching...' : statusLoading ? '🔄 Checking...' : '🔍 Search';

  const persistAnonFlag = useCallback(() => {
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('anonSearchUsed', 'true');
      }
    } catch (err) {
      // ignore storage errors
    }
    setAnonSearchUsed(true);
  }, []);

  const fetchStatus = useCallback(async (email) => {
    const resp = await axios.get(`${API_BASE_URL}/api/subscription-status`, {
      params: { email },
      headers: localStorage.getItem('premiumAccessToken')
        ? { 'x-premium-access-token': localStorage.getItem('premiumAccessToken') }
        : undefined
    });
    const payload = resp?.data || {};
    if (!payload.subscription) return null;
    const paid = !!(payload.subscription?.paid ?? payload.paid);
    const verified = !!(payload.subscription?.verified ?? payload.verified ?? paid);
    if (paid && payload.access_token) {
      localStorage.setItem('premiumAccessToken', payload.access_token);
      window.dispatchEvent(new Event('premium-access-changed'));
    }
    return {
      ...payload.subscription,
      paid,
      verified
    };
  }, []);

  const refreshSubscriptionInfo = useCallback(async (email) => {
    const normalizedEmail = (email || '').toString().trim().toLowerCase();
    if (!normalizedEmail) {
      setSubscriptionInfo(null);
      return null;
    }

    setStatusLoading(true);
    try {
      let info = await fetchStatus(normalizedEmail);
      if (info && !info.verified && !info.paid) {
        try {
          await axios.get(`${API_BASE_URL}/api/sync-subscription`, { params: { email: normalizedEmail } });
          info = await fetchStatus(normalizedEmail) || info;
        } catch (syncErr) {
          console.warn('Sync subscription failed', syncErr);
        }
      }
      setSubscriptionInfo(info || null);
      return info || null;
    } catch (statusErr) {
      console.error('Failed to fetch subscription status', statusErr);
      setSubscriptionInfo(null);
      return null;
    } finally {
      setStatusLoading(false);
    }
  }, [fetchStatus]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!subscriptionEmail) {
        setSubscriptionInfo(null);
        return;
      }
      if (cancelled) return;
      await refreshSubscriptionInfo(subscriptionEmail);
    })();
    return () => { cancelled = true; };
  }, [subscriptionEmail, refreshSubscriptionInfo]);

  useEffect(() => {
    if (statusLoading) {
      setSubscriptionStatus('Checking subscription status…');
      return;
    }

    if (subscriptionEmail && subscriptionInfo?.paid) {
      setSubscriptionStatus('You are a premium member — unlimited searches, instant alerts, and all 13 premium tools unlocked.');
      try {
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('isPremiumUser', 'true');
        }
      } catch (err) {
        // ignore storage errors
      }
      return;
    }

    if (subscriptionEmail && subscriptionInfo && !subscriptionInfo.paid) {
      setSubscriptionStatus('Upgrade to unlock instant email + web push alerts and all 13 premium tools.');
      return;
    }

    if (!subscriptionEmail) {
      setSubscriptionStatus(
        anonSearchUsed
          ? 'Your complimentary demo search is complete. Upgrade for instant alerts + all 13 premium tools.'
          : 'Enjoy one complimentary visa lookup — no signup required.'
      );
      return;
    }

    setSubscriptionStatus('Already paid? Restore your access. Otherwise, upgrade to Premium.');
  }, [statusLoading, subscriptionEmail, subscriptionInfo, anonSearchUsed]);

  const ensureLocalSubscription = (patch = {}) => {
    setSubscriptionInfo(prev => {
      const base = prev || { verified: false, paid: false };
      const next = { ...base, ...patch };
      return next;
    });
  };

  const handleLogout = () => {
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('isPremiumUser');
      }
    } catch (e) {
      // ignore storage errors
    }
    localStorage.removeItem('userEmail');
    setSubscriptionEmail('');
    setSubscriptionInfo(null);
    setSubscriptionStatus('');
    setResults(null);
    setError('');
    setCheckoutOpen(false);
    setReturningModalOpen(false);
  };

  const handleSwitchAccount = () => {
    handleLogout();
    setSubscriptionStatus('Sign in with your subscribed email to continue.');
    setReturningModalOpen(true);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!normalizedRef) {
      setError('Please enter a reference number');
      return;
    }

    const searchingAnon = !subscriptionEmail;
    if (searchingAnon && anonSearchUsed) {
      setSubscriptionStatus('Your free demo search is complete. Upgrade for instant alerts + all 13 premium tools.');
      setCheckoutOpen(true);
      return;
    }

    if (!searchingAnon) {
      const latestInfo = await refreshSubscriptionInfo(subscriptionEmail);
      if (!latestInfo?.paid) {
        setSubscriptionStatus('Already paid? Restore your access. Otherwise, upgrade to Premium.');
        if (localStorage.getItem('premiumAccessToken') || subscriptionInfo?.paid) {
          setReturningModalOpen(true);
        }
        return;
      }
    }

    setLoading(true);
    setError('');
    setResults(null);
    const lookupTier = searchingAnon ? 'public' : 'premium';
    trackAnalyticsEvent('status_lookup_started', { lookup_tier: lookupTier });

    try {
      const config = {};
      if (!searchingAnon) {
        config.headers = {
          'X-User-Email': subscriptionEmail,
          'X-Premium-Access-Token': localStorage.getItem('premiumAccessToken') || ''
        };
      }
      const response = await axios.get(`${API_BASE_URL}/api/search/${normalizedRef}`, config);
      trackAnalyticsEvent('status_lookup_completed', { lookup_tier: lookupTier });
      if (response.data) {
        setResults([response.data]);
        if (searchingAnon && !anonSearchUsed) {
          persistAnonFlag();
          setSubscriptionStatus('Love instant updates? Upgrade for instant alerts + all 13 premium tools.');
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        trackAnalyticsEvent('status_lookup_completed', { lookup_tier: lookupTier });
        setResults([{ reference_number: normalizedRef, decision: 'Pending' }]);
        setError('');
        if (searchingAnon && !anonSearchUsed) {
          persistAnonFlag();
        }
      } else if (status === 403 && err.response?.data?.accessRequired) {
        setSubscriptionStatus('Restore your premium access using the email address from your purchase.');
        setReturningModalOpen(true);
        setError('');
      } else if (status === 403 && err.response?.data?.anonymousLimitReached) {
        persistAnonFlag();
        setSubscriptionStatus('Your free demo search is done. Upgrade for instant alerts + all 13 premium tools.');
        setCheckoutOpen(true);
        setError('');
      } else if (status === 403 && err.response?.data?.upgradeRequired) {
        setSubscriptionStatus('Upgrade to keep monitoring instantly and unlock all 13 premium tools.');
        setCheckoutOpen(true);
        setError('');
      } else if (status === 403 && err.response?.data?.allowed_reference) {
        setError(`Your plan currently covers visa reference ${err.response.data.allowed_reference}.`);
      } else {
        console.error('Search failed:', err);
        if (!err.response && !navigator.onLine) {
          setError('You appear to be offline. Reconnect to search visa updates.');
        } else {
          setError('Search failed. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckoutSuccess = async () => {
    setCheckoutOpen(false);
    ensureLocalSubscription({ paid: true, verified: true });
    if (subscriptionEmail) {
      await refreshSubscriptionInfo(subscriptionEmail);
    }
    setSubscriptionStatus('Payment successful! Unlimited searches unlocked.');
  };

  const openCheckout = async () => {
    setCheckoutOpen(true);
  };

  const handleReturningSuccess = async (email, payload = {}) => {
    const normalized = (email || '').trim().toLowerCase();
    localStorage.setItem('userEmail', normalized);
    setSubscriptionEmail(normalized);
    if (payload?.subscription) {
      setSubscriptionInfo(payload.subscription);
    } else {
      refreshSubscriptionInfo(normalized);
    }
    setSubscriptionStatus('Welcome back! Premium access restored.');
    setReturningModalOpen(false);
  };

  const openReturningModal = () => {
    setReturningModalOpen(true);
  };

  const closeReturningModal = () => {
    setReturningModalOpen(false);
  };

  return (
      <div className="search-container">
          <div className="search-card">
                <div className="search-header">
                    <h3>🔍 Visa Status Checker</h3>
                    <p className="search-subtitle">Enter your reference number to check status</p>
                </div>

                <form onSubmit={handleSearch} className="search-form">
                    <div className="input-group">
                        <input
                            type="text"
                            value={refNumber}
                            onChange={(e) => setRefNumber(e.target.value)}
                            placeholder="Enter reference number (e.g., 123456789)"
                            className="search-input"
                            aria-label="Reference number"
                            disabled={loading}
                        />
                        <button type="submit" className="search-button" disabled={searchDisabled}>
                            {searchButtonLabel}
                        </button>
                    </div>
                </form>

                <div className="entry-selector">
                    {subscriptionEmail ? (
                        <div className="account-actions">
                            <span className="account-label">Signed in as {subscriptionEmail}</span>
                            {!subscriptionInfo?.paid && (
                                <Fragment>
                                    <button type="button" className="search-button alt" onClick={openReturningModal} disabled={loading || statusLoading}>
                                        Already paid? Restore access
                                    </button>
                                    <button type="button" className="search-button alt" onClick={openCheckout} disabled={loading || statusLoading}>
                                        Upgrade for ₹349 – Alerts + 13 Tools
                                    </button>
                                </Fragment>
                            )}
                            <button
                                type="button"
                                className="search-button alt"
                                onClick={handleLogout}
                                disabled={loading || statusLoading}
                            >
                                Logout
                            </button>
                        </div>
                    ) : (
                        <Fragment>
                            <button
                                type="button"
                                className="search-button alt"
                                onClick={openReturningModal}
                                disabled={loading || statusLoading}
                            >
                                Returning subscriber
                            </button>
                            <button
                                type="button"
                                className="search-button alt"
                                onClick={openCheckout}
                                disabled={loading || statusLoading}
                            >
                                Upgrade for ₹349 – Alerts + 13 Tools
                            </button>
                        </Fragment>
                    )}
                </div>

                {subscriptionStatus && (
                    <div
                        className={`subscription-banner ${
                            subscriptionInfo && (subscriptionInfo.paid || subscriptionInfo.verified)
                                ? 'success'
                                : 'warning'
                        }`}
                    >
                        <span className="banner-copy">{subscriptionStatus}</span>
                        {subscriptionInfo && (
                            <button type="button" className="switch-account" onClick={handleSwitchAccount}>
                                Not you? Switch account
                            </button>
                        )}
                    </div>
                )}

                {error && (
                    <div className="error-message">
                        ⚠️ {error}
                    </div>
                )}

                {!subscriptionEmail && anonSearchUsed && (
                    <div className="subscription-banner warn">
                        <div>
                            <strong>Demo complete.</strong> Upgrade now to keep tracking this visa with instant alerts + all 13 premium tools.
                        </div>
                        <button type="button" className="switch-account" onClick={openCheckout}>
                            Upgrade for ₹349 – Alerts + 13 Tools
                        </button>
                    </div>
                )}

                {results && results.length > 0 && (
                    <div className="results-section">
                        <h4>📋 Search Results</h4>
                        {results.map((result, index) => (
                            <div key={index} className="result-item">
                                <div className="result-header">
                                    <span className="ref-number">Ref: {result.reference_number || refNumber}</span>
                                    <span className={`status ${String(result.decision || 'Pending').toLowerCase()}`}>
                                        {result.decision || 'Pending'}
                                    </span>
                                </div>
                                <div className="result-details">
                                    {String(result.decision).toLowerCase() === 'approved' && (
                                        <p>🎉 <strong>Congratulations!</strong> Your visa has been <strong>Approved</strong>.</p>
                                    )}
                                    {String(result.decision).toLowerCase() === 'refused' && (
                                        <p>😔 <strong>We are extremely sorry.</strong> Your visa has been <strong>Refused</strong>.</p>
                                    )}
                                    {(!result.decision || String(result.decision).toLowerCase() === 'pending') && (
                                        <p>⏳ <strong>Pending.</strong> Please check again soon for updates.</p>
                                    )}
                                    {result.reported_date && (
                                        <p><strong>Last Update:</strong> {String(result.reported_date).split('T')[0]}</p>
                                    )}
                                </div>
                            </div>
                        ))}

                    </div>
                )}

                {subscriptionEmail && subscriptionInfo?.paid && (
                    <div className="upgrade-offer">
                        <h5>🚀 Premium unlocked</h5>
                        <p>Unlimited visa lookups + instant alerts + access to all 13 premium tools.</p>
                        <button
                            type="button"
                            className="search-button"
                            onClick={openCheckout}
                        >
                            Manage subscription
                        </button>
                    </div>
                )}
            </div>

            <ReturningUserModal
                open={returningModalOpen}
                onClose={closeReturningModal}
                onSuccess={handleReturningSuccess}
                prefillEmail={subscriptionEmail}
            />
            <CheckoutModal
                open={checkoutOpen}
                onClose={() => setCheckoutOpen(false)}
                email={subscriptionEmail}
                defaultReferences={(() => {
                    if (!subscriptionInfo) return [];
                    const refs = [];
                    if (subscriptionInfo.reference_number) refs.push(subscriptionInfo.reference_number);
                    if (normalizedRef && !refs.includes(normalizedRef)) refs.push(normalizedRef);
                    return refs;
                })()}
                requireSubscriber={!!(subscriptionInfo && (subscriptionInfo.verified || subscriptionInfo.paid))}
                onSuccess={handleCheckoutSuccess}
            />
        </div>
    );
};

export default SearchBar;
